import logger from './logger';
import { TimePickerSettings, CompanyTimeSettings, BufferTimeSettings } from '../types';

/**
 * FL-59: 時間ピッカーのデフォルト設定
 * 陸送会社向けの標準的な時間制限設定を提供
 */

/**
 * 引き取り時間のデフォルト設定
 * 営業時間内（7:00-20:00）で、深夜・早朝を除外
 */
export const DEFAULT_PICKUP_TIME_SETTINGS: TimePickerSettings = {
  minTime: '07:00',
  maxTime: '20:00',
  excludedHours: [
    '00:00',
    '01:00',
    '02:00',
    '03:00',
    '04:00',
    '05:00',
    '06:00', // 深夜・早朝
    '21:00',
    '22:00',
    '23:00', // 夜間
  ],
  minimumDuration: 60, // 1時間
};

/**
 * 搬入時間のデフォルト設定
 * 引き取り時間より1時間後から21:00まで
 */
export const DEFAULT_DELIVERY_TIME_SETTINGS: TimePickerSettings = {
  minTime: '08:00', // 引き取り最早時間（7:00）+ 最小間隔（1時間）
  maxTime: '21:00',
  excludedHours: [
    '00:00',
    '01:00',
    '02:00',
    '03:00',
    '04:00',
    '05:00',
    '06:00',
    '07:00', // 深夜・早朝
    '22:00',
    '23:00', // 深夜
  ],
  minimumDuration: 60, // 1時間
};

/**
 * 陸送員勤務時間のデフォルト設定
 * 早朝6:00から深夜22:00まで、深夜時間帯は除外
 */
export const DEFAULT_WORKING_HOURS_SETTINGS: TimePickerSettings = {
  minTime: '06:00',
  maxTime: '22:00',
  excludedHours: [
    '00:00',
    '01:00',
    '02:00',
    '03:00',
    '04:00',
    '05:00', // 深夜・早朝
    '23:00', // 深夜
  ],
  minimumDuration: 60, // 1時間（勤務時間では使用されないが一貫性のため）
};

/**
 * FL-81: バッファ時間のデフォルト設定
 * 陸送員のスケジュール管理でのバッファ時間を定義
 */
export const DEFAULT_BUFFER_TIME_SETTINGS: BufferTimeSettings = {
  betweenCasesBuffer: 30, // 案件間移動バッファ（30分）
  lunchTimeBuffer: {
    startTime: '12:00', // 昼休み開始時間
    endTime: '13:00', // 昼休み終了時間
    bufferMinutes: 60, // 昼休み前後のバッファ（60分）
  },
  longDistanceBuffer: {
    local: 15, // 市内移動（15分）
    prefectural: 30, // 県内移動（30分）
    crossPrefecture: 60, // 県外移動（60分）
  },
  dailyPreparationBuffer: 15, // 1日最初の準備時間（15分）
};

/**
 * デフォルトの陸送会社時間設定を生成
 * @param companyId - 陸送会社ID
 * @returns デフォルト設定済みのCompanyTimeSettings
 */
export const createDefaultCompanyTimeSettings = (companyId: string): CompanyTimeSettings => {
  const now = new Date().toISOString();

  return {
    id: `company-time-${companyId}`,
    companyId,
    pickupTimeSettings: { ...DEFAULT_PICKUP_TIME_SETTINGS },
    deliveryTimeSettings: { ...DEFAULT_DELIVERY_TIME_SETTINGS },
    bufferTimeSettings: { ...DEFAULT_BUFFER_TIME_SETTINGS }, // FL-81: バッファ時間設定を追加
    createdAt: now,
    updatedAt: now,
  };
};

/**
 * 時間が除外時間に含まれているかチェック
 * @param time - チェックする時間（HH:mm形式）
 * @param excludedHours - 除外時間リスト
 * @returns 除外時間の場合true
 */
export const isTimeExcluded = (time: string, excludedHours: string[]): boolean => {
  return excludedHours.includes(time);
};

/**
 * 時間が設定範囲内かチェック
 * @param time - チェックする時間（HH:mm形式）
 * @param minTime - 最小時間
 * @param maxTime - 最大時間
 * @returns 範囲内の場合true
 */
export const isTimeInRange = (time: string, minTime: string, maxTime: string): boolean => {
  return time >= minTime && time <= maxTime;
};

/**
 * 時間設定に基づいて有効な時間選択肢を生成
 * @param settings - 時間ピッカー設定
 * @param intervalMinutes - 時間間隔（分）
 * @returns 有効な時間選択肢の配列
 */
export const generateValidTimeOptions = (
  settings: TimePickerSettings,
  intervalMinutes: number = 5
): string[] => {
  const options: string[] = [];

  // 最小時間と最大時間を分に変換（NaN対策）
  const [minHour, minMinute] = settings.minTime.split(':').map(Number);
  const [maxHour, maxMinute] = settings.maxTime.split(':').map(Number);

  // NaNチェック
  if (isNaN(minHour) || isNaN(minMinute) || isNaN(maxHour) || isNaN(maxMinute)) {
    logger.warn('Invalid time format in settings:', {
      minTime: settings.minTime,
      maxTime: settings.maxTime,
    });
    return [];
  }

  const startMinutes = minHour * 60 + minMinute;
  const endMinutes = maxHour * 60 + maxMinute;

  // 指定間隔で時間を生成
  for (
    let totalMinutes = startMinutes;
    totalMinutes <= endMinutes;
    totalMinutes += intervalMinutes
  ) {
    const hour = Math.floor(totalMinutes / 60);
    const minute = totalMinutes % 60;
    const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

    // 除外時間でない場合のみ追加
    if (!isTimeExcluded(timeString, settings.excludedHours)) {
      options.push(timeString);
    }
  }

  return options;
};

/**
 * FL-81: バッファ時間を加味した終了時間を計算
 * @param endTime - 基準終了時間（HH:mm形式）
 * @param bufferMinutes - バッファ時間（分）
 * @returns バッファ時間を加味した終了時間（HH:mm形式）
 */
export const addBufferToEndTime = (endTime: string, bufferMinutes: number): string => {
  const [hour, minute] = endTime.split(':').map(Number);

  // NaNチェック
  if (isNaN(hour) || isNaN(minute)) {
    logger.warn('Invalid time format:', endTime);
    return endTime;
  }

  const totalMinutes = hour * 60 + minute + bufferMinutes;
  const newHour = Math.floor(totalMinutes / 60) % 24; // 24時間表記でラップ
  const newMinute = totalMinutes % 60;

  return `${newHour.toString().padStart(2, '0')}:${newMinute.toString().padStart(2, '0')}`;
};

/**
 * FL-81: バッファ時間を加味した開始時間を計算
 * @param startTime - 基準開始時間（HH:mm形式）
 * @param bufferMinutes - バッファ時間（分）
 * @returns バッファ時間を加味した開始時間（HH:mm形式）
 */
export const subtractBufferFromStartTime = (startTime: string, bufferMinutes: number): string => {
  const [hour, minute] = startTime.split(':').map(Number);

  // NaNチェック
  if (isNaN(hour) || isNaN(minute)) {
    logger.warn('Invalid time format:', startTime);
    return startTime;
  }

  let totalMinutes = hour * 60 + minute - bufferMinutes;

  // 負の値になった場合は前日扱い（0:00より前は23:xx）
  if (totalMinutes < 0) {
    totalMinutes += 24 * 60;
  }

  const newHour = Math.floor(totalMinutes / 60) % 24;
  const newMinute = totalMinutes % 60;

  return `${newHour.toString().padStart(2, '0')}:${newMinute.toString().padStart(2, '0')}`;
};

/**
 * FL-81: 昼休み時間との重複をチェック
 * @param startTime - 開始時間（HH:mm形式）
 * @param endTime - 終了時間（HH:mm形式）
 * @param lunchTimeSettings - 昼休み時間設定
 * @returns 昼休み時間と重複する場合true
 */
export const isOverlappingWithLunchTime = (
  startTime: string,
  endTime: string,
  lunchTimeSettings: { startTime: string; endTime: string }
): boolean => {
  const caseStartTime = startTime;
  const caseEndTime = endTime;
  const lunchStartTime = lunchTimeSettings.startTime;
  const lunchEndTime = lunchTimeSettings.endTime;

  /**
   * 時間重複判定ロジック:
   * 案件と昼休みが重複するのは以下の条件を満たす場合:
   * - 案件開始時刻 < 昼休み終了時刻 AND 案件終了時刻 > 昼休み開始時刻
   *
   * 例: 昼休み 12:00-13:00 の場合
   *   重複する: 案件 11:30-12:30 (11:30 < 13:00 AND 12:30 > 12:00)
   *   重複しない: 案件 11:00-12:00 (11:00 < 13:00 BUT 12:00 = 12:00)
   */
  return caseStartTime < lunchEndTime && caseEndTime > lunchStartTime;
};
